import React, { useState, useRef, useEffect, useContext } from "react";
import gsap from "gsap";
import { createPortal } from "react-dom";
import { LandingPageContext } from "../../contexts/LandingPageContext";
import { useTranslation } from "react-i18next";
import Card from "./Card";
import "./FirstSection.css";

const FirstSection = () => {
    const { firstSection, getScrollSmoother } = useContext(LandingPageContext);
    const [showCards, setShowCards] = useState(false);
    const [bgLoaded, setBgLoaded] = useState(false);
    const modalRef = useRef(null);
    const fModalRef = useRef(null);
    const { t } = useTranslation();
    const [showArrow, setShowArrow] = useState(true);
    const titleColor = '#A08B7A';

    const getText = (value) => {
        if (typeof value === 'string') return value;
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            const currentLang = 'de';
            return value[currentLang] || value.de || value.en || '';
        }
        return String(value || '');
    };

    const getStackedText = () => {
        if (Array.isArray(firstSection.stackedText)) return firstSection.stackedText;
        if (typeof firstSection.stackedText === 'object' && firstSection.stackedText !== null) {
            return firstSection.stackedText.de || firstSection.stackedText.en || [];
        }
        return [];
    };

    useEffect(() => {
        const img = new Image();
        img.src =  'https://res.cloudinary.com/dqssg8tws/image/upload/v1770105120/1_z71t8m.jpg';
        img.onload = () => setBgLoaded(true);
    }, [firstSection.backgroundImage]);

    useEffect(() => {
        if (!showCards) return;
        const cards = gsap.utils.toArray(".card");
        gsap.fromTo(cards, { y: 50, opacity: 0, scale: 0.9 }, { y: 0, opacity: 1, scale: 1, duration: 1, stagger: 0.15, ease: "back.out(1.7)" });
    }, [showCards]);

    useEffect(() => {
        if (!showCards) return;
        const modalEl = modalRef.current;
        const contentEl = fModalRef.current;
        if (!modalEl || !contentEl) return;
        const forwardIfEdge = (e) => {
            const x = e.clientX;
            const w = window.innerWidth;
            const edgePct = 0.10;
            const leftBoundary = w * edgePct;
            const rightBoundary = w * (1 - edgePct);
            if (x <= leftBoundary || x >= rightBoundary) {
                if (e.cancelable) e.preventDefault();
                contentEl.scrollBy({ top: e.deltaY, left: 0, behavior: 'auto' });
            }
        };
        modalEl.addEventListener('wheel', forwardIfEdge, { passive: false });
        return () => { modalEl.removeEventListener('wheel', forwardIfEdge); };
    }, [showCards]);

    useEffect(() => {
        if (!showCards) return;
        const contentEl = fModalRef.current;
        if (!contentEl) return;
        const checkScroll = () => {
            const threshold = 4;
            const isAtBottom = contentEl.scrollTop + contentEl.clientHeight >= contentEl.scrollHeight - threshold;
            const hasOverflow = contentEl.scrollHeight > contentEl.clientHeight + 1;
            const isTouch = (typeof window !== 'undefined') && (('ontouchstart' in window) || (navigator.maxTouchPoints && navigator.maxTouchPoints > 0));
            const isMobile = window.innerWidth <= 768 || isTouch;
            const shouldShow = !isAtBottom && (hasOverflow || isMobile);
            setShowArrow(shouldShow);
        };
        checkScroll();
        contentEl.addEventListener('scroll', checkScroll, { passive: true });
        window.addEventListener('resize', checkScroll);
        return () => {
            contentEl.removeEventListener('scroll', checkScroll);
            window.removeEventListener('resize', checkScroll);
        };
    }, [showCards]);

    const openModal = () => {
        setShowCards(true);
        setShowArrow(true);
        requestAnimationFrame(() => requestAnimationFrame(() => setShowArrow(true)));
        document.body.style.overflow = "hidden";
        const smoother = getScrollSmoother && getScrollSmoother();
        smoother?.paused(true);
    };

    const closeModal = () => {
        gsap.to(".card", { y: 50, opacity: 0, scale: 0.9, duration: 0.4, stagger: 0.1 });
        gsap.to(modalRef.current, { opacity: 0, duration: 0.4, onComplete: () => {
                setShowCards(false);
                setShowArrow(true);
                document.body.style.overflow = "auto";
                const smoother = typeof getScrollSmoother === 'function' ? getScrollSmoother() : null;
                if (!smoother) return;
                smoother.paused(false);
            }});
    };

    const titleText = (t && typeof t === 'function') ? (t('nav.idee') || t('idee')) : 'IDEE';

    return (
        <div className="first-section">
            <div className="first-section-title" id="firstSection" style={{ color: titleColor }}>
                {titleText}
            </div>
            <div className="hero-intro" style={{ backgroundImage: bgLoaded ? `url(${firstSection.backgroundImage || 'https://res.cloudinary.com/dqssg8tws/image/upload/v1770105120/1_z71t8m.jpg'})` : 'none', backgroundColor: '#eaeaea' }}>
                <div className="white-square-overlay" style={{ backgroundColor: firstSection.backgroundColor || '#ffffff' }}>
                    <div className="stacked-text" style={{ color: firstSection.stackedTextColor || '#000' }}>
                        {getStackedText().map((text, index) => (<div key={index}>{text}</div>))}
                    </div>
                </div>
                <div className="nav-square-left" style={{ color: firstSection.navTextColor || firstSection.textColor || '#ffffff' }}>
                    <div className="clickable-nav improved">
                        <div className="link-wrapper">
                            <button type="button" className="link-button" onClick={openModal} style={{ color: firstSection.navTextColor || firstSection.textColor || '#ffffff', background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: 'pointer' }}>
                                {getText(firstSection.navText)}
                            </button>
                        </div>
                    </div>
                    <p className="sub-title" style={{ color: firstSection.subTitleColor || firstSection.textColor || '#cccccc' }}>{getText(firstSection.subTitle)}</p>
                </div>

                {showCards && createPortal(
                    <div className="modal" ref={modalRef}>
                        <div className="f-modal-content" ref={fModalRef}>
                            <div className="modal-header" style={{ color: firstSection.modalHeaderColor || firstSection.textColor || '#fff' }}>
                                <div>{getText(firstSection.modalHeader).split('\n').map((line, index) => (<div key={index}>{line}</div>))}</div>
                                <button className="close-btn" onClick={closeModal} style={{ color: firstSection.modalHeaderColor || firstSection.textColor || '#ffffff' }}>×</button>
                            </div>
                            <div className="modal-body">
                                <Card />
                            </div>
                        </div>
                    </div>,
                    document.body
                )}

                {showCards && createPortal(
                    <div className={`scroll-arrow ${showArrow ? 'visible' : 'hidden'}`} aria-hidden="true">
                        <div className="rings" aria-hidden="true">
                            <span className="ring r1" />
                            <span className="ring r2" />
                            <span className="ring r3" />
                        </div>
                        <svg className="arrow-svg" width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true">
                            <g stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none">
                                <circle cx="12" cy="12" r="10" strokeOpacity="0.14" />
                                <path d="M12 7v6" />
                                <path d="M7.8 12.8L12 17l4.2-4.2" />
                            </g>
                        </svg>
                    </div>,
                    document.body
                )}
            </div>
        </div>
    );
};

export default FirstSection;
